import { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'

import { clone } from 'helpers/global.helper/global.helper'

//------ Filtering of dataset instuctions --------
function filterDatasetInstructionWithSources(
  datasetInstruction: any,
  items: UiConfigPropsItem[],
  activeTags: Set<string>,
  allTags: Set<string>,
  allTagsDict: { [unitName: string]: string[] }
): any {
  // Filters out sources in a DI with sources.
  const newSources: { source?: string; return_id: string }[] = []

  datasetInstruction.contract?.sources?.forEach((source: any) => {
    const sourceItem = items.find((item) => item.data_id === source.return_id)

    if (sourceItem && !sourceItem.fake) {
      // sources which have an item in the uiconfig will be included in filtering iftagsAreIncludedInFiltering(...) returns true

      if (tagsAreIncludedInFiltering(sourceItem?.tags ?? [], activeTags, allTags)) {
        newSources.push(source)
      }
    } else if (!sourceItem) {
      // sources without an item in the uiconfig will be included in filtering if the source has tags from digital twin model json, or if it has no tags at all
      const dataIdComponents = source.return_id.split('.')
      const sourceUnitName = dataIdComponents[dataIdComponents.length - 1]
      const sourceTags = getDigitalTwinTagsFromDataId(sourceUnitName, allTagsDict)
      if (sourceTags && tagsAreIncludedInFiltering(sourceTags, activeTags, allTags)) {
        newSources.push(source)
      }
    } else if (sourceItem.fake) {
      // is the found sourceItem from a calc variable, then it will be included in filtering if it has tags from the digital twin model json or its parent item

      const sourceTags = getTagsAccordingToTagsLogicForItem(sourceItem, allTagsDict)

      const sourceItemTags = sourceItem.tags ?? []
      const soughtTags = sourceItemTags.concat(sourceTags ?? [])

      if (tagsAreIncludedInFiltering(soughtTags, activeTags, allTags)) {
        newSources.push(source)
      }
    }
  })

  return {
    ...datasetInstruction,
    contract: {
      ...datasetInstruction.contract,
      sources: newSources,
    },
  } as DatasetInstruction
}

function filterDatasetInstructionWithoutSources(
  datasetInstruction: DatasetInstruction,
  items: UiConfigPropsItem[],
  activeTags: Set<string>,
  allTags: Set<string>,
  allTagsDict: { [unitName: string]: string[] }
): DatasetInstruction | undefined {
  const instructionItem = items.find((item) => item.data_id === datasetInstruction.return_id)

  if (instructionItem && !instructionItem.fake) {
    if (tagsAreIncludedInFiltering(instructionItem?.tags ?? [], activeTags, allTags)) {
      return datasetInstruction
    }
  } else if (!instructionItem && datasetInstruction.return_id) {
    //only include this item if it has sourcetags from model
    const sourceDataId = datasetInstruction.return_id.split('.')
    const unitName = sourceDataId[sourceDataId.length - 1]

    const sourceTags = getDigitalTwinTagsFromDataId(unitName, allTagsDict)

    if (sourceTags && tagsAreIncludedInFiltering(sourceTags, activeTags, allTags)) {
      return datasetInstruction
    }
  } else if (instructionItem?.fake) {
    //is the found sourceItem from a calc variable, then it will be included in filtering if it has tags from the digital twin model json or its parent item

    const sourceTags = getTagsAccordingToTagsLogicForItem(instructionItem, allTagsDict)
    if (tagsAreIncludedInFiltering(sourceTags, activeTags, allTags)) {
      return datasetInstruction
    }
  }

  return undefined
}

function filterDbSDatasetInstruction(
  datasetInstruction: DatasetInstruction,
  items: UiConfigPropsItem[],
  activeTags: Set<string>,
  allTags: Set<string>,
  allTagsDict: { [unitName: string]: string[] }
): DatasetInstruction | undefined {
  // Filters out sources in a DI with sources.
  const newInternalIds: string[] = []
  const contract = datasetInstruction.contract as DatasetContractDbS

  if (!contract?.metadata_filter?.include?.internal_id?.length) {
    return undefined
  }

  const idToItem: Record<string, UiConfigPropsItem> = {}
  items.forEach((item) => {
    idToItem[item.data_id] = item
  })

  const internalIds =
    typeof contract.metadata_filter.include.internal_id == 'string'
      ? [contract.metadata_filter.include.internal_id]
      : contract.metadata_filter.include.internal_id

  internalIds.forEach((internalId: string) => {
    const returnId = contract?.id_renaming?.[internalId] || internalId
    const returnIds = typeof returnId === 'string' ? [returnId] : returnId

    returnIds.forEach((returnId) => {
      const sourceItem = idToItem[returnId]
      if (sourceItem && !sourceItem.fake) {
        // sources which have an item in the uiconfig will be included in filtering iftagsAreIncludedInFiltering(...) returns true

        if (tagsAreIncludedInFiltering(sourceItem?.tags ?? [], activeTags, allTags)) {
          newInternalIds.push(internalId)
        }
      } else if (!sourceItem) {
        // sources without an item in the uiconfig will be included in filtering if the source has tags from digital twin model json, or if it has no tags at all
        const sourceDataId = internalId.split('.')
        const sourceTags = getDigitalTwinTagsFromDataId(sourceDataId, allTagsDict) // returns undefined if the source could not be found in the digital twin json model

        if (sourceTags && tagsAreIncludedInFiltering(sourceTags, activeTags, allTags)) {
          newInternalIds.push(internalId)
        }
      } else if (sourceItem.fake) {
        // is the found sourceItem from a calc variable, then it will be included in filtering if it has tags from the digital twin model json or its parent item
        const sourceDataId = internalId.split('.')
        const sourceTags = getDigitalTwinTagsFromDataId(sourceDataId, allTagsDict) // returns undefined if the source could not be found in the digital twin json model
        const sourceItemTags = sourceItem.tags ?? []
        const soughtTags = sourceItemTags.concat(sourceTags ?? [])

        if (tagsAreIncludedInFiltering(soughtTags, activeTags, allTags)) {
          newInternalIds.push(internalId)
        }
      }
    })
  })

  if (!newInternalIds.length) {
    return undefined
  }

  const newDatasetInstruction = clone(datasetInstruction)
  ;(newDatasetInstruction.contract as DatasetContractDbS).metadata_filter.include.internal_id = newInternalIds

  return newDatasetInstruction
}

function tagsAreIncludedInFiltering(tags: string[], activeTags: Set<string>, allTags: Set<string>): boolean {
  const filteredTags = tags.filter((tag) => allTags.has(tag)) //filter out the tags that may have been misspelled or plainly does not exist in the digital twin model json

  if (filteredTags.length === 0) {
    //if there are no tags, return true.
    return true
  } else if (filteredTags.every((tag) => activeTags.has(tag))) {
    // if all of the tags are currently filtered in, return true
    return true
  }

  return false
}

export function filterDatasetInstructionsNotShown(
  datasetInstructions: any[],
  items: UiConfigPropsItem[],
  activeTags: Set<string>,
  allTags: Set<string>,
  allTagsDict: { [unitName: string]: string[] }
): DatasetInstruction[] {
  const newDatasetInstructions: any[] = []

  datasetInstructions.forEach((instruction) => {
    if (instruction.type === 'calc') {
      for (let i = 0; i < items?.length; i++) {
        const item = items[i]

        if (tagsAreIncludedInFiltering(item?.tags ?? [], activeTags, allTags)) {
          newDatasetInstructions.push(instruction)
          break
        }
      }
    } else if (instruction.contract?.sources && instruction.contract.sources.length > 0) {
      const datasetInstructionWithSources = filterDatasetInstructionWithSources(
        instruction,
        items,
        activeTags,
        allTags,
        allTagsDict
      )

      const instructionAlreadyExistsInDatasetInstructions = newDatasetInstructions.find((datasetInstruction) => {
        datasetInstruction.contract &&
          datasetInstructionWithSources.contract &&
          datasetInstruction.filter &&
          datasetInstructionWithSources.filter &&
          datasetInstruction.return_id &&
          datasetInstructionWithSources.return_id &&
          datasetInstruction.type &&
          datasetInstructionWithSources.type
      })

      if (instructionAlreadyExistsInDatasetInstructions) {
        const merge = <T>(a: T[], b: T[], predicate: (a: T, b: T) => boolean = (a, b) => a === b): T[] => {
          const c = clone(a) // copy to avoid side effects
          // add all items from B to copy C if they're not already present
          b.forEach((bItem) => (c.some((cItem) => predicate(bItem, cItem)) ? null : c.push(bItem)))
          return c
        }

        const newSources = merge(
          instructionAlreadyExistsInDatasetInstructions.contract.sources,
          datasetInstructionWithSources.contract.sources,
          (a: any, b: any) => a.return_id === b.return_id
        )
        newDatasetInstructions.push({
          ...instructionAlreadyExistsInDatasetInstructions,
          contract: { ...instructionAlreadyExistsInDatasetInstructions.contract, sources: newSources },
        })
      } else {
        newDatasetInstructions.push(datasetInstructionWithSources)
      }
    } else if (instruction.type === 'dbs') {
      const filteredDatasetInstruction = filterDbSDatasetInstruction(
        instruction,
        items,
        activeTags,
        allTags,
        allTagsDict
      )
      if (filteredDatasetInstruction) {
        newDatasetInstructions.push(filteredDatasetInstruction)
      }
    } else {
      const datasetInstructionWithoutSources = filterDatasetInstructionWithoutSources(
        instruction,
        items,
        activeTags,
        allTags,
        allTagsDict
      )

      if (datasetInstructionWithoutSources) {
        newDatasetInstructions.push(datasetInstructionWithoutSources)
      }
    }
  })
  return newDatasetInstructions
}

//------ Fetching tags --------

export function getDigitalTwinTagsFromDataId(
  unitName: string | string[],
  allTagsDict: { [unitName: string]: string[] }
): string[] | undefined {
  //getting tags from digital twin based on data_id

  const tryTagsForEachPrefix = (prefixes: string[]): string[] | undefined => {
    //TODO: We might need to add distinguishment between different outputs, e.g unit.var_panna.el and unit.var_panna.fjv
    for (const prefix of prefixes) {
      const tagsFromDTModel = allTagsDict[prefix + '.' + unitName]

      if (tagsFromDTModel) {
        return tagsFromDTModel
      }
    }
  }

  const tags = tryTagsForEachPrefix(['unit', 'exchange', 'balance', 'commodity'])
  return tags ?? []
}

export function getItemsFromVariableCalcs(
  datasetInstructions: any[],
  items: UiConfigPropsItem[],
  allTagsDict: { [unitName: string]: string[] }
): UiConfigPropsItem[] {
  const variableItems: UiConfigPropsItem[] = []
  datasetInstructions.forEach((instruction) => {
    if (instruction.type === 'calc') {
      const calcItem = items.find((item) => item.data_id === instruction.return_id)
      const tags = calcItem ? getTagsAccordingToTagsLogicForItem(calcItem, allTagsDict) : []

      instruction.contract.variables?.forEach((variable: any) => {
        const variableExistsAsItem = items.find((item) => item.data_id === variable)
        if (typeof variable === 'string' && !variableExistsAsItem) {
          variableItems.push({ data_id: variable, tags: tags, fake: true, title: variable })
        }
      })
    }
  })

  const uniqueVariableItems = Array.from(new Set(variableItems))

  return uniqueVariableItems
}

export function getTagsAccordingToTagsLogicForItem(
  item: UiConfigPropsItem,
  allTagsDict: { [unitName: string]: string[] }
): string[] {
  let itemTags: string[] = []

  if (item.tags && item.tags.length !== 0) {
    //if item has tags, the tags will be used
    itemTags = Array.from(new Set(item.tags))
  } else if (item.get_tags_from) {
    //if item has get_tags_from, the tags will be fetched from the get_tags_from (could be several in the dict)
    itemTags = []
    item.get_tags_from.forEach((name) => {
      //unit.p7.input.fjv
      const dictTags = allTagsDict[name]
      if (!dictTags) {
        itemTags.push(...(getDigitalTwinTagsFromDataId(name, allTagsDict ?? {}) ?? []))
      } else {
        itemTags.push(...(dictTags ?? []))
      }
    })
    itemTags = Array.from(new Set(itemTags))
  } else {
    //else the tags will be fetched from the data_id
    const dataIdComponents = item?.data_id?.split('.')

    if (dataIdComponents?.length === 0 || !dataIdComponents) {
      itemTags = []
    } else {
      const unitName = dataIdComponents[dataIdComponents.length - 1] //should be t.ex var_panna
      itemTags = getDigitalTwinTagsFromDataId(unitName, allTagsDict ?? {}) ?? []
    }
  }

  return itemTags
}

export function getItemsWithTags(
  uiConfigPropsItems: UiConfigPropsItem[],
  allTagsDict: { [unitName: string]: string[] }
): UiConfigPropsItem[] {
  //TODO: This mapping can only function if the data_ids are in the format of "production.heat.osv"
  const itemsWithTags = uiConfigPropsItems.map((item) => {
    const itemTags: string[] = getTagsAccordingToTagsLogicForItem(item, allTagsDict)

    return {
      ...item,
      tags: itemTags,
    }
  })

  return itemsWithTags
}

export function getFuelFromTags(tags: string[] | undefined): string {
  //always returns the fuel from tag
  const stringArrayWithFuel = tags?.find((tag) => tag.split('/')[0].includes('fuel'))

  if (stringArrayWithFuel) {
    return stringArrayWithFuel.split('/')[1]
  }
  return ''
}

export function getAvailableFilters(
  items: string[][],
  activeFilters: string[],
  hiddenFilters: Set<string>,
  checkedFilters: Record<string, Record<string, boolean>>
): Set<string> {
  // A filter is visibile, if and only if changing it will show/hide more items.
  // If other filters prevent an item from being shown/hidden when a filter is changed, that filter is irrelevant for that item.
  // If a filter is not relevant for any item, there is no need to show it.

  //Find the checked filters in the filter component
  const selectedFilters: string[] = []
  Object.values(checkedFilters).forEach((topLevel) => {
    Object.entries(topLevel).forEach(([key, value]) => {
      if (value === true) {
        selectedFilters.push(key)
      }
    })
  })

  const output = new Set<string>()

  items.forEach((item) => {
    const inactiveFiltersForItem = new Set(item)
    activeFilters.forEach((filter) => inactiveFiltersForItem.delete(filter))
    if (inactiveFiltersForItem.size === 0) {
      // All filters are ACTIVE for this combination, so all filters are relevant (since changing one of them will hide the item).
      item.forEach((filter) => output.add(filter))
    } else if (inactiveFiltersForItem.size === 1) {
      // Only one filter is INACTIVE for this combination, so that filter is relevant (since changing it will show the item).
      inactiveFiltersForItem.forEach((filter) => output.add(filter))
    }
    //items with 2 or more inactive filters are not relevant for the output, since changing one of them will neither show nor hide the item.
  })

  // Ensure selected filters are included in output so the selected filters aren't hidden
  selectedFilters.forEach((filter) => output.add(filter))

  // Hidden filters should not be included when we decide which top level filters to show.
  // If all items in a list are in the 'hidden_filters' list, the top level filter should not be shown.
  hiddenFilters.forEach((filter) => output.delete(filter))
  return output
}

export function allowsPartialTagMatching(objectTags: string[], filterTags: string[]): boolean {
  //This function is a special check for modules that do NOT have group filtering and checks what data should be displayed. With this function, it allows objects to be displayed if at least one of the toplevel tags is in filterTags. E.g., if an event or setting has the tags [subgrid/a, subgrid/b] and you filter for subgrid/a, it will be displayed even if subgrid/b is not selected in the filter. NOTE: This should only be used for modules that do NOT have group filtering since it will disturb calculation for grouped filters.
  // Group object tags by category (part before '/')
  const tagsByCategory = objectTags.reduce(
    (acc, tag) => {
      const category = tag.split('/')[0]
      if (!acc[category]) acc[category] = []
      acc[category].push(tag)
      return acc
    },
    {} as Record<string, string[]>
  )
  // Check each category has at least one visible tag that matches filters
  return Object.values(tagsByCategory).every((categoryTags) => {
    return categoryTags.length === 0 || categoryTags.some((tag) => filterTags.includes(tag))
  })
}

// Parses tags from properties based on current priorities.
export function getPropertyTags(
  properties: ConfigurableProperty[] | UiConfigPropsItem[],
  currentPriorities: SystemSettingPriorityLevel[]
): string[] {
  const tags: Set<string> = new Set<string>()

  properties
    .filter(
      (property) =>
        !Array.isArray(property.available_priorities) ||
        property.available_priorities?.some((priority) =>
          currentPriorities.some((currentPriority) => currentPriority.priority_level === priority)
        )
    )
    .forEach((property) => (property.tags ?? []).forEach((tag) => tags.add(tag)))

  return Array.from(tags)
}

export type Options = {
  [key: string]: string[]
}

export function parseTags(tags: string[]): Options {
  if (!Array.isArray(tags)) {
    // Can happen if someone writes the digital twin json on an incorrect format.
    return {}
  }

  let out = {}
  tags.forEach((tag) => {
    out = parseTag(tag, out)
  })

  return out
}

export function parseTag(tag: string, current: Options = {}): Options {
  tag = tag.replace(/^\/+/g, '') // Remove leading slashes

  if (tag === '') {
    return current
  }

  const [topLevelTag, subLevelTag] = tag.split(/\/(.*)/)

  const existingTagsForTopLevel = topLevelTag in current ? current[topLevelTag] : []

  if (subLevelTag === undefined || subLevelTag === '') {
    return { ...current, [topLevelTag]: existingTagsForTopLevel }
  } else {
    return { ...current, [topLevelTag]: [subLevelTag, ...existingTagsForTopLevel] }
  }
}

export function matchesSomeTagFromEveryTopLevel(tagsToCheck: string[], filterTags: string[]): boolean {
  if (tagsToCheck.every((tag) => filterTags.includes(tag))) {
    return true
  }

  return false
}

export function getTopLevelOptions(
  availableFilters: Set<string>,
  options: Options,
  hideFilters?: string[]
): { value: string; label: string }[] {
  const availableTopLevelTags = new Set(Array.from(availableFilters).map((f) => f.split('/')[0]))

  return Object.keys(options) // Don't show top levels that don't have any sub levels.
    .filter((option) => {
      return !hideFilters?.includes(option) && options[option].length > 0 && availableTopLevelTags.has(option)
    })
    .map((option) => {
      const optionValue = options[option]
      const value = typeof optionValue === 'string' ? optionValue : option
      return {
        value,
        label: option,
      }
    })
}
